import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostsStore, Post, PostStatus, PostType } from '../store/posts';
import { useProjectsStore } from '../store/projects';
import { useFlightsStore, Flight } from '../store/flights';
import { usePublishingStore } from '../store/publishing';
import { PostDetailModal } from '../components/PostDetailModal';
import { CreatePostModal, CreatePostData } from '../components/CreatePostModal';
import { PageHeader } from '../components/PageHeader';
import { getMediaUrl } from '../services/api';
import { POST_STATUS, PostStatus as PostStatusType, GENERIC_STATS } from '../config/statusConfig';
import { StatsCard, StatsCardGrid } from '../components/StatsCard';

type SortField = 'scheduled_for' | 'created_at' | 'status';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'list' | 'grid';

export function PostsPage() {
  const { t } = useTranslation(['posts', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { posts, isLoading, fetchPosts, deletePost, updatePost, cancelPost, duplicatePost, batchDelete, batchUpdate, clearPosts } = usePostsStore();
  const { projects, fetchProjects, selectedProjectId, setSelectedProjectId } = useProjectsStore();
  const { flights, fetchFlights } = useFlightsStore();
  const { publishToInstagram, isPublishing, publishingPostId } = usePublishingStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PostStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<PostType | 'all'>('all');
  const [flightFilter, setFlightFilter] = useState<string>('all');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('scheduled_for');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Check for project query parameter
  useEffect(() => {
    const projectParam = searchParams.get('project');
    if (projectParam && projects.length > 0) {
      const projectExists = projects.some(p => p.id === projectParam);
      if (projectExists) {
        setSelectedProjectId(projectParam);
      }
    }
  }, [searchParams, projects, setSelectedProjectId]);

  // Check for flight filter query parameter
  useEffect(() => {
    const flightParam = searchParams.get('flight');
    if (flightParam) {
      setFlightFilter(flightParam);
    }
  }, [searchParams]);

  // Check for post deep-link query parameter - open post modal
  useEffect(() => {
    const postParam = searchParams.get('post');
    if (postParam && posts.length > 0 && !editingPost) {
      const post = posts.find(p => p.id === postParam);
      if (post) {
        setEditingPost(post);
        // Clear the URL parameter after opening
        navigate('/posts', { replace: true });
      }
    }
  }, [searchParams, posts, editingPost, navigate]);

  // Load projects
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Auto-select first project
  useEffect(() => {
    if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId, setSelectedProjectId]);

  // Load posts and flights when project changes
  useEffect(() => {
    if (selectedProjectId) {
      fetchPosts(selectedProjectId, { limit: 500 });
      fetchFlights(selectedProjectId);
    } else {
      clearPosts();
    }
  }, [selectedProjectId, fetchPosts, fetchFlights, clearPosts]);

  // Filter and sort posts
  const filteredPosts = useMemo(() => {
    return posts
      .filter((post) => {
        // Search filter
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          const matchesText = post.text?.toLowerCase().includes(query);
          const matchesId = post.display_id.toLowerCase().includes(query);
          const matchesHashtag = post.hashtags?.some(h => h.toLowerCase().includes(query));
          if (!matchesText && !matchesId && !matchesHashtag) return false;
        }
        // Status filter
        if (statusFilter !== 'all' && post.status !== statusFilter) return false;
        // Type filter
        if (typeFilter !== 'all' && post.post_type !== typeFilter) return false;
        // Flight filter
        if (flightFilter !== 'all') {
          if (flightFilter === 'none' && post.flight_id !== null) return false;
          if (flightFilter !== 'none' && post.flight_id !== flightFilter) return false;
        }
        // Platform filter
        if (platformFilter !== 'all' && !post.platforms?.includes(platformFilter)) return false;
        return true;
      })
      .sort((a, b) => {
        let comparison = 0;
        switch (sortField) {
          case 'scheduled_for':
            comparison = new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime();
            break;
          case 'created_at':
            comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            break;
          case 'status':
            comparison = a.status.localeCompare(b.status);
            break;
        }
        return sortOrder === 'asc' ? comparison : -comparison;
      });
  }, [posts, searchQuery, statusFilter, typeFilter, flightFilter, platformFilter, sortField, sortOrder]);

  // Get flight by ID
  const getFlight = (flightId: string | null): Flight | undefined => {
    return flights.find(f => f.id === flightId);
  };

  // Format date
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Toggle post selection
  const togglePostSelection = (postId: string) => {
    setSelectedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  // Select all filtered posts
  const selectAll = () => {
    if (selectedPosts.size === filteredPosts.length) {
      setSelectedPosts(new Set());
    } else {
      setSelectedPosts(new Set(filteredPosts.map(p => p.id)));
    }
  };

  // Batch actions
  const handleBatchDelete = async () => {
    if (selectedPosts.size === 0) return;
    if (!confirm(t('posts:confirmDeleteMultiple', { count: selectedPosts.size }))) return;

    await batchDelete(Array.from(selectedPosts));
    setSelectedPosts(new Set());
    if (selectedProjectId) {
      fetchPosts(selectedProjectId, { limit: 500 });
    }
  };

  const handleBatchStatusChange = async (status: 'draft' | 'scheduled') => {
    if (selectedPosts.size === 0) return;

    await batchUpdate(Array.from(selectedPosts), { status });
    setSelectedPosts(new Set());
    if (selectedProjectId) {
      fetchPosts(selectedProjectId, { limit: 500 });
    }
  };

  // Publish a post to Instagram
  const handlePublish = async (post: Post, platform: 'instagram' | 'facebook') => {
    if (!post.media_urls || post.media_urls.length === 0) {
      alert(t('posts:instagramNeedsMedia'));
      return;
    }

    if (!post.platforms?.includes(platform)) {
      alert(t('posts:notConfiguredForPlatform', { platform: platform === 'instagram' ? 'Instagram' : 'Facebook' }));
      return;
    }

    if (platform === 'instagram') {
      const result = await publishToInstagram(post.id);
      if (result.success) {
        alert(t('posts:successOnInstagram'));
        if (selectedProjectId) {
          fetchPosts(selectedProjectId, { limit: 500 });
        }
      } else {
        alert(`${t('posts:detail.error')}: ${result.error}`);
      }
    } else {
      alert(t('posts:facebookNeedsPage'));
    }
  };

  // Stats
  const stats = useMemo(() => {
    return {
      total: posts.length,
      draft: posts.filter(p => p.status === 'draft').length,
      scheduled: posts.filter(p => p.status === 'scheduled').length,
      published: posts.filter(p => p.status === 'published').length,
      failed: posts.filter(p => p.status === 'failed').length,
    };
  }, [posts]);

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  // Handle creating new post
  const handleCreatePost = async (data: CreatePostData) => {
    await usePostsStore.getState().createPost(data);
    if (selectedProjectId) {
      fetchPosts(selectedProjectId, { limit: 500 });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        icon={
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        }
        title={t('posts:title')}
        subtitle={t('posts:subtitle')}
        breadcrumbs={selectedProject ? [
          { label: t('common:nav.projects'), href: '/projects' },
          { label: selectedProject.name, href: `/projects/${selectedProject.id}` },
        ] : [{ label: t('common:nav.dashboard'), href: '/' }]}
        actions={
          <div className="flex items-center gap-3">
            {/* Project Selector */}
            {projects.length > 0 && (
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
              >
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => selectedProjectId && setIsCreateModalOpen(true)}
              disabled={!selectedProjectId}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('posts:createPost')}
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="mb-6">
        <StatsCardGrid columns={5}>
          <StatsCard
            value={stats.total}
            label={t('posts:filters.all')}
            config={GENERIC_STATS.posts}
            isActive={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          />
          <StatsCard
            value={stats.draft}
            label={t('posts:status.draft')}
            config={POST_STATUS.draft}
            isActive={statusFilter === 'draft'}
            onClick={() => setStatusFilter('draft')}
          />
          <StatsCard
            value={stats.scheduled}
            label={t('posts:status.scheduled')}
            config={POST_STATUS.scheduled}
            isActive={statusFilter === 'scheduled'}
            onClick={() => setStatusFilter('scheduled')}
          />
          <StatsCard
            value={stats.published}
            label={t('posts:status.published')}
            config={POST_STATUS.published}
            isActive={statusFilter === 'published'}
            onClick={() => setStatusFilter('published')}
          />
          {stats.failed > 0 && (
            <StatsCard
              value={stats.failed}
              label={t('posts:status.failed')}
              config={POST_STATUS.failed}
              isActive={statusFilter === 'failed'}
              onClick={() => setStatusFilter('failed')}
            />
          )}
        </StatsCardGrid>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('posts:search.placeholder')}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as PostType | 'all')}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('posts:filters.allTypes')}</option>
            <option value="feed">{t('posts:postType.feed')}</option>
            <option value="reel">{t('posts:postType.reel')}</option>
            <option value="story">{t('posts:postType.story')}</option>
            <option value="carousel">{t('posts:postType.carousel')}</option>
          </select>

          {/* Flight Filter */}
          <select
            value={flightFilter}
            onChange={(e) => setFlightFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('posts:filters.allFlights')}</option>
            <option value="none">{t('posts:filters.withoutFlight')}</option>
            {flights.map((flight) => (
              <option key={flight.id} value={flight.id}>
                {flight.name}
              </option>
            ))}
          </select>

          {/* Platform Filter */}
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('posts:filters.allPlatforms')}</option>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
          </select>

          {/* Sort */}
          <select
            value={`${sortField}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortField(field as SortField);
              setSortOrder(order as SortOrder);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="scheduled_for-desc">{t('posts:sort.newest')}</option>
            <option value="scheduled_for-asc">{t('posts:sort.oldest')}</option>
            <option value="created_at-desc">{t('posts:sort.lastCreated')}</option>
            <option value="status-asc">{t('posts:sort.status')}</option>
          </select>

          {/* View Mode */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 ${viewMode === 'list' ? 'bg-gray-100' : 'bg-white hover:bg-gray-50'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'bg-white hover:bg-gray-50'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
          </div>

          {/* Clear Filters */}
          {(statusFilter !== 'all' || typeFilter !== 'all' || flightFilter !== 'all' || platformFilter !== 'all' || searchQuery) && (
            <button
              onClick={() => {
                setStatusFilter('all');
                setTypeFilter('all');
                setFlightFilter('all');
                setPlatformFilter('all');
                setSearchQuery('');
              }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              {t('posts:filters.reset')}
            </button>
          )}
        </div>
      </div>

      {/* Batch Actions */}
      {selectedPosts.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-center justify-between">
          <span className="text-sm text-blue-700">
            {t('posts:batch.selected', { count: selectedPosts.size })}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleBatchStatusChange('draft')}
              className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('posts:batch.asDraft')}
            </button>
            <button
              onClick={() => handleBatchStatusChange('scheduled')}
              className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('posts:batch.asScheduled')}
            </button>
            <button
              onClick={handleBatchDelete}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              {t('common:delete')}
            </button>
            <button
              onClick={() => setSelectedPosts(new Set())}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              {t('common:cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Posts List/Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">{t('common:loading')}</div>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {posts.length === 0 ? t('posts:empty.title') : t('posts:empty.titleFiltered')}
          </h3>
          <p className="text-gray-500 mb-4">
            {posts.length === 0
              ? t('posts:empty.description')
              : t('posts:empty.descriptionFiltered')}
          </p>
          <button
            onClick={() => selectedProjectId && setIsCreateModalOpen(true)}
            disabled={!selectedProjectId}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {t('posts:createPost')}
          </button>
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase">
            <div className="col-span-1 flex items-center">
              <input
                type="checkbox"
                checked={selectedPosts.size === filteredPosts.length && filteredPosts.length > 0}
                onChange={selectAll}
                className="rounded border-gray-300"
              />
            </div>
            <div className="col-span-4">Post</div>
            <div className="col-span-2">{t('common:status')}</div>
            <div className="col-span-2">{t('posts:scheduledFor')}</div>
            <div className="col-span-2">{t('posts:flight')}</div>
            <div className="col-span-1">{t('common:actions')}</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-100">
            {filteredPosts.map((post) => {
              const flight = getFlight(post.flight_id);
              const statusConfig = POST_STATUS[post.status as PostStatusType];

              return (
                <div
                  key={post.id}
                  className={`px-4 py-3 grid grid-cols-12 gap-4 items-center hover:bg-gray-50 transition-colors border-l-[5px] ${statusConfig.accent} ${statusConfig.cardBg}`}
                >
                  {/* Checkbox */}
                  <div className="col-span-1">
                    <input
                      type="checkbox"
                      checked={selectedPosts.has(post.id)}
                      onChange={() => togglePostSelection(post.id)}
                      className="rounded border-gray-300"
                    />
                  </div>

                  {/* Post Content */}
                  <div
                    className="col-span-4 flex items-center gap-3 cursor-pointer"
                    onClick={() => setEditingPost(post)}
                  >
                    {post.media_urls && post.media_urls.length > 0 ? (
                      <img
                        src={getMediaUrl(post.media_urls[0])}
                        alt=""
                        className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-900 truncate">
                        {post.text || <span className="text-gray-400 italic">{t('posts:mediaOnly')}</span>}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">{post.display_id}</span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-gray-500">{t(`posts:postType.${post.post_type}`)}</span>
                        {post.platforms?.map((platform) => (
                          <span
                            key={platform}
                            className={`px-1.5 py-0.5 text-xs rounded ${
                              platform === 'facebook'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-pink-100 text-pink-700'
                            }`}
                          >
                            {platform === 'facebook' ? 'FB' : 'IG'}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
                      {t(`posts:status.${post.status}`)}
                    </span>
                  </div>

                  {/* Scheduled Date */}
                  <div className="col-span-2">
                    <span className="text-sm text-gray-600">{formatDate(post.scheduled_for)}</span>
                  </div>

                  {/* Flight */}
                  <div className="col-span-2 min-w-0">
                    {flight ? (
                      <Link
                        to={`/flights/${flight.id}`}
                        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 max-w-full"
                        onClick={(e) => e.stopPropagation()}
                        title={flight.name}
                      >
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: flight.color || '#3B82F6' }}
                        />
                        <span className="truncate">{flight.name}</span>
                      </Link>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center gap-1">
                    {/* Publish Button - only for scheduled/draft posts with media */}
                    {(post.status === 'scheduled' || post.status === 'draft' || post.status === 'failed') &&
                     post.media_urls && post.media_urls.length > 0 &&
                     post.platforms?.includes('instagram') && (
                      <button
                        onClick={() => handlePublish(post, 'instagram')}
                        disabled={isPublishing && publishingPostId === post.id}
                        className={`p-1.5 rounded ${
                          isPublishing && publishingPostId === post.id
                            ? 'text-pink-400 cursor-wait'
                            : 'text-gray-400 hover:text-pink-600'
                        }`}
                        title={t('posts:publishToInstagram')}
                      >
                        {isPublishing && publishingPostId === post.id ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                          </svg>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => setEditingPost(post)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                      title={t('common:edit')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={async () => {
                        if (confirm(t('posts:confirmDelete'))) {
                          await deletePost(post.id);
                          if (selectedProjectId) {
                            fetchPosts(selectedProjectId, { limit: 500 });
                          }
                        }
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      title={t('common:delete')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredPosts.map((post) => {
            const flight = getFlight(post.flight_id);
            const statusConfig = POST_STATUS[post.status as PostStatusType];

            return (
              <div
                key={post.id}
                onClick={() => setEditingPost(post)}
                className={`rounded-xl shadow-sm border border-gray-200 border-l-[5px] ${statusConfig.accent} ${statusConfig.cardBg} overflow-hidden hover:shadow-md transition-shadow cursor-pointer group`}
              >
                {/* Thumbnail */}
                <div className="aspect-square bg-gray-100 relative">
                  {post.media_urls && post.media_urls.length > 0 ? (
                    <img
                      src={getMediaUrl(post.media_urls[0])}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}

                  {/* Selection checkbox */}
                  <div className="absolute top-2 left-2">
                    <input
                      type="checkbox"
                      checked={selectedPosts.has(post.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        togglePostSelection(post.id);
                      }}
                      className="rounded border-gray-300 bg-white/80"
                    />
                  </div>

                  {/* Media count badge */}
                  {post.media_urls && post.media_urls.length > 1 && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/50 text-white text-xs rounded">
                      +{post.media_urls.length - 1}
                    </div>
                  )}

                  {/* Status badge */}
                  <div className="absolute bottom-2 left-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
                      {t(`posts:status.${post.status}`)}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3">
                  <p className="text-sm text-gray-900 line-clamp-2 mb-2">
                    {post.text || <span className="text-gray-400 italic">{t('posts:mediaOnly')}</span>}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{formatDate(post.scheduled_for).split(',')[0]}</span>
                    <div className="flex items-center gap-1">
                      {flight && (
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: flight.color || '#3B82F6' }}
                        />
                      )}
                      {post.platforms?.map((platform) => (
                        <span
                          key={platform}
                          className={`px-1 py-0.5 rounded text-[10px] ${
                            platform === 'facebook'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-pink-100 text-pink-700'
                          }`}
                        >
                          {platform === 'facebook' ? 'FB' : 'IG'}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Results Count */}
      {filteredPosts.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          {t('posts:resultsCount', { filtered: filteredPosts.length, total: posts.length })}
        </div>
      )}

      {/* Post Detail Modal */}
      {editingPost && selectedProjectId && (
        <PostDetailModal
          post={editingPost}
          isOpen={true}
          onClose={() => {
            setEditingPost(null);
          }}
          onUpdate={async (postId, data) => {
            await updatePost(postId, data);
            setEditingPost(null);
            fetchPosts(selectedProjectId, { limit: 500 });
          }}
          onDelete={async (postId, options) => {
            const result = await deletePost(postId, options);
            if (result.success) {
              setEditingPost(null);
              fetchPosts(selectedProjectId, { limit: 500 });
            }
            return result;
          }}
          onCancel={async (postId) => {
            await cancelPost(postId);
            setEditingPost(null);
            fetchPosts(selectedProjectId, { limit: 500 });
          }}
          onDuplicate={async (postId) => {
            await duplicatePost(postId);
            setEditingPost(null);
            fetchPosts(selectedProjectId, { limit: 500 });
          }}
          flights={flights.map(f => ({ id: f.id, name: f.name, color: f.color || '#3B82F6' }))}
        />
      )}

      {/* Create Post Modal */}
      {selectedProjectId && (
        <CreatePostModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          projectId={selectedProjectId}
          onCreate={handleCreatePost}
          flights={flights.map(f => ({
            id: f.id,
            name: f.name,
            display_id: f.display_id,
            status: f.status,
          }))}
        />
      )}
    </div>
  );
}
